import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api.js';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import SlideToggle from '../../components/SlideToggle.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';

const blank = {
  name: '', contactName: '', phone: '', afterHoursPhone: '', fax: '', email: '',
  address: '', city: '', province: '', postalCode: '', country: 'Canada', status: 'Active', notes: '',
  licenseNumber: '', bondNumber: '', directionHandled: '', portsOfEntry: '', emanifestAccount: '', ourAccountNumber: '', standardFee: '',
  brokerCountry: 'Canada',
};

function fromBroker(b) {
  if (!b) return blank;
  return {
    name: b.name || '', contactName: b.contact_name || '', phone: b.phone || '', afterHoursPhone: b.after_hours_phone || '', fax: b.fax || '', email: b.email || '',
    address: b.address || '', city: b.city || '', province: b.province || '', postalCode: b.postal_code || '', country: b.country || 'Canada',
    status: b.status || 'Active', notes: b.notes || '',
    licenseNumber: b.license_number || '', bondNumber: b.bond_number || '', directionHandled: b.direction_handled || '',
    portsOfEntry: b.ports_of_entry || '', emanifestAccount: b.emanifest_account || '', ourAccountNumber: b.our_account_number || '', standardFee: b.standard_fee ?? '',
    // Which side of the border this broker is licensed for — distinct from
    // the office address's `country` above — is what an order form's
    // Customs Broker picker filters on.
    brokerCountry: b.broker_country || 'Canada',
  };
}

const TABS = ['Basic Information', 'Border Crossing Details', 'Documents'];

export default function CustomsBrokerFormModal({ customsBroker, onClose, onSaved }) {
  const [savedId, setSavedId] = useState(customsBroker?.id || null);
  const [form, setForm] = useState(() => fromBroker(customsBroker));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [manualAddress, setManualAddress] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Documents (tab 2) ────────────────────────────────────────────────────
  const [checklist, setChecklist] = useState(null);
  const loadChecklist = () => { if (savedId) api(`/dispatch/customs-brokers/${savedId}/checklist`).then(setChecklist).catch(() => setChecklist([])); };
  useEffect(() => { if (tab === 2 && savedId) loadChecklist(); }, [tab, savedId]);

  const toggleChecklistItem = async (item, isChecked) => {
    await api(`/dispatch/customs-brokers/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked, expiryDate: item.expiry_date } });
    loadChecklist();
  };
  const setChecklistExpiry = async (item, expiryDate) => {
    await api(`/dispatch/customs-brokers/${savedId}/checklist/${item.doc_type_id}`, { method: 'POST', body: { isChecked: !!item.is_checked, expiryDate } });
    loadChecklist();
  };
  const addDocType = async () => {
    const name = window.prompt('New document type name');
    if (!name || !name.trim()) return;
    await api('/dispatch/customs-broker-doc-types', { method: 'POST', body: { name, isRequired: false } });
    loadChecklist();
  };

  const persist = async () => {
    if (!form.name.trim()) { setError('Customs broker name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const saved = savedId
        ? await api(`/dispatch/customs-brokers/${savedId}`, { method: 'PATCH', body: form })
        : await api('/dispatch/customs-brokers', { method: 'POST', body: form });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => { if (await persist()) setTab(t => Math.min(t + 1, TABS.length - 1)); };
  const reset = () => setForm(customsBroker ? fromBroker(customsBroker) : blank);

  // Portal to <body>: this modal is also opened from inside the order form,
  // whose CSS (.create-load input { appearance: none; ... }) would otherwise
  // strip checkbox/radio styling. Rendering at body level keeps it identical
  // to how it looks in Control.
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{customsBroker?.id ? 'Edit customs broker' : 'New customs broker'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>{t}</button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <label style={{ display: 'block', marginBottom: 18 }}>Broker Name / Company<br/><input value={form.name} onChange={e => set('name', e.target.value)} /></label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Contact Name<br/><input value={form.contactName} onChange={e => set('contactName', e.target.value)} /></label>
                <label>Phone<br/><PhoneInput value={form.phone} onChange={v => set('phone', v)} /></label>
                <label>After-Hours / Emergency Phone<br/><PhoneInput value={form.afterHoursPhone} onChange={v => set('afterHoursPhone', v)} placeholder="for after-hours crossings" /></label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Fax<br/><PhoneInput value={form.fax} onChange={v => set('fax', v)} /></label>
                <label>Email<br/><input value={form.email} onChange={e => set('email', e.target.value)} /></label>
              </div>

              <label>Address<br/>
                <AddressAutocomplete value={form.address} onChange={val => set('address', val)}
                  onPlaceSelected={({ address, city, province, postalCode, country }) => {
                    setForm(f => ({ ...f, address, city, province, postalCode, country }));
                    setManualAddress(true);
                  }} />
              </label>
              <button type="button" className="link-toggle" style={{ marginBottom: manualAddress ? 12 : 18 }} onClick={() => setManualAddress(m => !m)}>
                {manualAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
              </button>
              {manualAddress && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>City<br/><input value={form.city} onChange={e => set('city', e.target.value)} /></label>
                  <label>Province / State<br/><input value={form.province} onChange={e => set('province', e.target.value)} /></label>
                  <label>Postal / Zip<br/><input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} /></label>
                  <label>Country<br/>
                    <select value={form.country} onChange={e => set('country', e.target.value)}>
                      <option>Canada</option><option>United States</option>
                    </select>
                  </label>
                </div>
              )}

              <label style={{ display: 'block', maxWidth: 320, marginBottom: 18 }}>Status<br/>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  <option>Active</option><option>Inactive</option>
                </select>
              </label>

              <label style={{ display: 'block' }}>Notes<br/><input value={form.notes} onChange={e => set('notes', e.target.value)} /></label>
            </>
          )}

          {tab === 1 && (
            <>
              <label style={{ display: 'block', maxWidth: 220, marginBottom: 18 }}>Broker Type<br/>
                <div style={{ marginTop: 4 }}>
                  <SlideToggle value={form.brokerCountry} onChange={v => set('brokerCountry', v)}
                    leftValue="Canada" rightValue="US" leftLabel="Canadian Broker" rightLabel="US Broker" />
                </div>
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Broker License Number<br/><input value={form.licenseNumber} onChange={e => set('licenseNumber', e.target.value)} placeholder="CBSA or CBP license #" /></label>
                <label>Bond Number<br/><input value={form.bondNumber} onChange={e => set('bondNumber', e.target.value)} placeholder="continuous bond, if applicable" /></label>
              </div>

              <label style={{ display: 'block', maxWidth: 320, marginBottom: 18 }}>Direction Handled<br/>
                <select value={form.directionHandled} onChange={e => set('directionHandled', e.target.value)}>
                  <option value="">—</option>
                  <option>Import to Canada</option>
                  <option>Import to US</option>
                  <option>Export</option>
                  <option>Both / All Directions</option>
                </select>
              </label>

              <label style={{ display: 'block', marginBottom: 18 }}>Ports of Entry / Border Crossings Covered<br/>
                <input value={form.portsOfEntry} onChange={e => set('portsOfEntry', e.target.value)} placeholder="e.g. Detroit–Windsor, Buffalo–Fort Erie" />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>eManifest Account (ACI/ACE)<br/><input value={form.emanifestAccount} onChange={e => set('emanifestAccount', e.target.value)} placeholder="carrier code / account used for e-Manifest" /></label>
                <label>Our Account # with this Broker<br/><input value={form.ourAccountNumber} onChange={e => set('ourAccountNumber', e.target.value)} /></label>
              </div>

              <label style={{ display: 'block', maxWidth: 280 }}>Standard Fee per Entry ($)<br/><input type="number" value={form.standardFee} onChange={e => set('standardFee', e.target.value)} /></label>
            </>
          )}

          {tab === 2 && (
            !savedId ? <div className="empty">Save the customs broker first to track document checklist items.</div>
            : checklist === null ? <div className="empty">Loading...</div>
            : (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
                  Check a document once it's on file and set its expiry where relevant. This list is specific to customs brokers.
                </div>
                {checklist.length === 0 && <div className="empty">No document types yet — add Power of Attorney (POA), Broker License, Bond Certificate, or a Service Agreement below.</div>}
                {checklist.map(item => (
                  <div className="checklist-row" key={item.doc_type_id}>
                    <input type="checkbox" checked={!!item.is_checked} style={{ width: 'auto' }} onChange={e => toggleChecklistItem(item, e.target.checked)} />
                    <div style={{ flex: 1 }}>{item.name}</div>
                    {!!item.is_required && <span className="checklist-req-badge">Required</span>}
                    <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 700 }}>EXPIRY</span>
                    <input type="date" value={item.expiry_date || ''} style={{ width: 150 }} onChange={e => setChecklistExpiry(item, e.target.value)} />
                  </div>
                ))}
                <button type="button" onClick={addDocType} style={{ marginTop: 14 }}>+ Add document type</button>
              </>
            )
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : customsBroker ? 'Save changes' : 'Create customs broker'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
